import React from 'react'
import { graphql } from 'gatsby'

import { PrismicPageDataBodyFaqCta } from 'src/typings/generated/graphql'

import FaqCta, {
  FaqCtaContent,
  FaqCtaItem,
} from 'src/components/organisms/ctas/faqCta'

/**
 * Enum for the different types of items that can be displayed in the FAQ CTA slice's document list.
 */
export enum FaqCtaItemType {
  /**
   * A document link item, with a label, link and optional icon.
   */
  DocumentLink = 'Document Link',
  /**
   * A divider item, which is a horizontal line that separates document link items.
   */
  Divider = 'Divider',
}

type Props = {
  slice: PrismicPageDataBodyFaqCta
}

const FaqCtaSlice = ({ slice }: Props) => {
  const content: FaqCtaContent = {
    anchor: slice.primary.anchor ?? undefined,
    headline: slice.primary.headline ?? undefined,
    image: slice.primary.image?.gatsbyImageData ?? undefined,
    imageAlt: slice.primary.image?.alt ?? undefined,
    link: slice.primary.link ?? undefined,
    linkText: slice.primary.link_text ?? undefined,
    items: slice.items.map(
      item =>
        ({
          item_type: item.item_type ?? FaqCtaItemType.DocumentLink,
          link: item.document_link ?? undefined,
          linkIcon: item.document_link_icon?.gatsbyImageData ?? undefined,
          linkIconAlt: item.document_link_icon?.alt ?? undefined,
          linkText: item.document_link_text ?? undefined,
        } as FaqCtaItem)
    ),
  }

  return <FaqCta content={content} />
}

export default FaqCtaSlice

export const FaqCtaFragment = graphql`
  fragment PageDataBodyFaqCta on PrismicPageDataBodyFaqCta {
    primary {
      anchor
      headline
      link_text
      link {
        ...BasicLinkFragment
      }
      image {
        gatsbyImageData
        alt
      }
    }
    items {
      item_type
      document_link_text
      document_link {
        ...BasicLinkFragment
      }
      document_link_icon {
        gatsbyImageData(aspectRatio: 1, height: 25, width: 25)
        alt
      }
    }
  }
`
